import { useEffect, useRef } from 'react';
import { FieldErrors, FieldValues, UseControllerProps } from 'react-hook-form';

import { ValueType } from 'components/DebounceSelect';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { SyncApproach } from 'types/Integration';
import { NormalizedLoad, TMSLocation } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { integrationNameMap } from 'types/enums/Integrations';
import {
  GenericCompanySearchableFields,
  injectSelectedObject,
  locationSearchHandler,
  mapLocationsToSelectOptions,
  normalizeExternalTMSID,
  resolveSelectedItem,
  shouldUpgradeTMSRecord,
} from 'utils/loadInfoAndBuilding';

import { RHFDebounceSelect } from './RHFDebounceSelect';

type RHFLocationSelectProps<TFieldValues extends FieldValues = NormalizedLoad> =
  Pick<
    UseControllerProps<TFieldValues>,
    'name' | 'rules' | 'control' | 'disabled'
  > & {
    label?: string; // Default = 'Location'
    errors: FieldErrors;
    locations: Maybe<TMSLocation[]>;
    setLocations: React.Dispatch<React.SetStateAction<Maybe<TMSLocation[]>>>;
    isLoadingLocations: boolean;
    handleRefreshLocations?: () => void;
    labelClassName?: string;
    required?: boolean; // Default = true
    placeholder?: string; // Default = 'Search by name or address'
    tmsID?: number; // Optional TMS ID, will use from context if not provided
    customerID?: number;
    /** When set (e.g. 3G form), locations search is filtered to this customer. Used when customerID is not available. */
    customerExternalTmsId?: string;
    valueRenderer?: (selectedOption: ValueType) => ValueType; // Optional custom value renderer
    labelIsName?: boolean; // Whether to show location name in dropdown options (default: false, shows address)
    customActionButtons?: React.ReactNode; // Optional custom buttons to render next to refresh button
    aiHighlightedExternalTMSID?: string; // If provided, the matching option will be highlighted with AI sparkle icon
    // Called with the location the rep picks, e.g. to keep a field that depends on it in step.
    onLocationSelected?: (location: TMSLocation) => void;
  };

export function RHFLocationSelect<
  TFieldValues extends FieldValues = NormalizedLoad,
>({
  name,
  label = 'Location',
  disabled,
  control,
  errors,
  rules,
  locations,
  setLocations,
  isLoadingLocations,
  handleRefreshLocations,
  labelClassName,
  required = true,
  placeholder = 'Search by name or address',
  tmsID,
  customerID,
  customerExternalTmsId,
  valueRenderer,
  labelIsName = false,
  customActionButtons,
  aiHighlightedExternalTMSID,
  onLocationSelected,
}: RHFLocationSelectProps<TFieldValues>) {
  const { tmsIntegrations } = useServiceFeatures();

  const effectiveTmsID = tmsID ?? tmsIntegrations?.[0]?.id;

  const selectedIntegration = effectiveTmsID
    ? tmsIntegrations?.find((t) => t.id === effectiveTmsID)
    : tmsIntegrations?.[0];

  // When a customer is selected (3G, GlobalTranz, etc.), the parent always passes a scoped list.
  // The ref must track that list — not a stale integration-wide list from before the customer pick.
  const isCustomerScoped =
    (customerID != null && customerID > 0) ||
    (customerExternalTmsId != null &&
      String(customerExternalTmsId).trim() !== '');

  // Store original locations list to use for filtering when search is cleared
  const originalLocationsRef = useRef<Maybe<TMSLocation[]>>(null);
  const selectedSearchLocationRef = useRef<Maybe<TMSLocation>>(null);
  /** Last scoped customer key; reset when leaving scoped mode so re-selecting the same customer still replaces baseline. */
  const prevCustomerScopeKeyRef = useRef<string>('');

  const customerScopeKey = isCustomerScoped
    ? `${customerID ?? ''}:${String(customerExternalTmsId ?? '').trim()}`
    : '';

  // Update original locations ref when locations prop changes
  useEffect(() => {
    if (!locations) {
      return;
    }

    const shouldReplaceBaseline = (
      prevBaseline: Maybe<TMSLocation[]>,
      next: TMSLocation[]
    ) =>
      !prevBaseline ||
      next.length >= (prevBaseline.length || 0) ||
      !prevBaseline.length;

    if (!isCustomerScoped) {
      prevCustomerScopeKeyRef.current = '';
      if (shouldReplaceBaseline(originalLocationsRef.current, locations)) {
        originalLocationsRef.current = locations;
      }

      return;
    }

    // Scoped: on customer change only drop the baseline; do not read `locations` in this run (it can still
    // reflect mergedLocations + stale dataLocations before the parent clear + scoped fetch land).
    if (customerScopeKey !== prevCustomerScopeKeyRef.current) {
      prevCustomerScopeKeyRef.current = customerScopeKey;
      originalLocationsRef.current = null;

      return;
    }

    // First non-empty snapshot after a scope switch (or initial mount with customer already set).
    if (originalLocationsRef.current == null) {
      if (locations.length > 0) {
        originalLocationsRef.current = locations;
      }

      return;
    }

    // Same customer: grow-only so search-driven setLocations() cannot shrink the baseline.
    if (shouldReplaceBaseline(originalLocationsRef.current, locations)) {
      originalLocationsRef.current = locations;
    }
  }, [locations, isCustomerScoped, customerScopeKey]);

  const handleLocationSelection = (
    value: unknown,
    selectedRawItem?: unknown
  ) => {
    if (value == null) {
      return;
    }

    const valueToUse =
      typeof value === 'object' && value !== null && 'value' in value
        ? (value as { value: string }).value
        : value;

    const id = normalizeExternalTMSID(valueToUse);
    if (!id) {
      return;
    }

    const selectedLocation = resolveSelectedItem<TMSLocation>(
      id,
      locations,
      selectedRawItem,
      value
    );
    if (!selectedLocation?.externalTMSID) {
      return;
    }

    onLocationSelected?.(selectedLocation);

    const baseline = originalLocationsRef.current;
    // If baseline is not seeded yet, never treat as "from search" (avoids wrong inject + reset paths).
    const isFromSearch =
      baseline != null &&
      !baseline.some((loc) => normalizeExternalTMSID(loc.externalTMSID) === id);
    const baselineRow = baseline?.find(
      (loc) => normalizeExternalTMSID(loc.externalTMSID) === id
    );
    const isSparseUpgrade =
      baselineRow != null &&
      shouldUpgradeTMSRecord(selectedLocation, baselineRow);

    if (isFromSearch || isSparseUpgrade) {
      selectedSearchLocationRef.current = selectedLocation;
    } else {
      selectedSearchLocationRef.current = null;
    }

    // Inject new rows or upgrade sparse baseline stubs with richer search results.
    setLocations((prev) => injectSelectedObject(selectedLocation, prev ?? []));
    if (isSparseUpgrade && originalLocationsRef.current) {
      originalLocationsRef.current = injectSelectedObject(
        selectedLocation,
        originalLocationsRef.current
      );
    }
  };

  const handleLocationSearch = async (
    field: GenericCompanySearchableFields,
    value: string
  ) => {
    if (!effectiveTmsID) {
      return [];
    }

    const locationsToSearch = isCustomerScoped
      ? (originalLocationsRef.current ?? locations ?? [])
      : originalLocationsRef.current || locations;

    return locationSearchHandler({
      tmsID: effectiveTmsID,
      locations: locationsToSearch,
      setLocations,
      field,
      value,
      customerID,
      customerExternalTmsId,
    });
  };

  const handleResetOptions = () => {
    const selected = selectedSearchLocationRef.current;
    selectedSearchLocationRef.current = null;
    if (selected) {
      // Use originalLocationsRef as base so full list is restored (prev may only contain search results)
      setLocations(
        injectSelectedObject(selected, originalLocationsRef.current ?? [])
      );

      return;
    }

    if (originalLocationsRef.current) {
      setLocations(originalLocationsRef.current);
    }
  };

  const isRefreshDisabled =
    selectedIntegration?.locationSyncApproach === SyncApproach.FullSync ||
    selectedIntegration?.locationSyncApproach === SyncApproach.NotSupported ||
    selectedIntegration?.locationSyncApproach ===
      SyncApproach.SequentialFullSync;

  const formattedTMSName = selectedIntegration?.name
    ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore name is TMS enum or other matching type
      integrationNameMap[selectedIntegration.name] || selectedIntegration.name
    : 'your TMS';

  let refreshTooltip = isRefreshDisabled
    ? `Not seeing a recently added location? Due to ${formattedTMSName} constraints, ${formattedTMSName} gets new locations daily.`
    : undefined;

  if (
    isRefreshDisabled &&
    selectedIntegration?.implementsCreateLocation &&
    refreshTooltip
  ) {
    refreshTooltip +=
      ' You can also create this location directly in Drumkit to use it immediately.';
  }

  return (
    <RHFDebounceSelect
      required={required}
      name={name}
      label={label}
      disabled={disabled}
      control={control}
      errors={errors}
      rules={rules}
      data={locations}
      isLoading={isLoadingLocations}
      refreshHandler={handleRefreshLocations}
      labelClassName={labelClassName}
      resetOptionsHandler={handleResetOptions}
      refreshDisabled={isRefreshDisabled}
      refreshTooltip={refreshTooltip}
      fetchOptions={handleLocationSearch}
      mapOptions={(data) => mapLocationsToSelectOptions(data, labelIsName)}
      showSearchParamDropdown={false}
      searchFieldDefault='nameAddress'
      placeholder={placeholder}
      parentOnChange={handleLocationSelection}
      callFieldOnChangeWithParent={true}
      valueRenderer={valueRenderer}
      customActionButtons={customActionButtons}
      aiHighlightedExternalTMSID={aiHighlightedExternalTMSID}
    />
  );
}
